import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { 
  ArrowLeft, MapPin, Plus, X, Save, Droplets, Home, AlertTriangle 
} from 'lucide-react'
import { createPaddock, getPaddocks, getRanch } from '../../services/api'

export default function PaddockForm({ ranchId }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  const [error, setError] = useState('')
  const [formData, setFormData] = useState({
    name: '',
    management_type: 'mmg',
    has_kraal: false,
    has_water: true,
  })
  
  const [coordinates, setCoordinates] = useState([
    { lat: '', lng: '' },
    { lat: '', lng: '' },
    { lat: '', lng: '' },
  ])
  
  const [calculatedArea, setCalculatedArea] = useState(null)
  const [boundaryWarning, setBoundaryWarning] = useState('')

  // Fetch ranch to get boundary coordinates
  const { data: ranch } = useQuery({
    queryKey: ['ranch', ranchId],
    queryFn: () => getRanch(ranchId),
  })

  // Fetch existing paddocks to generate next code
  const { data: existingPaddocks } = useQuery({
    queryKey: ['paddocks', ranchId],
    queryFn: () => getPaddocks(ranchId),
  })

  // Check if point is inside polygon (ray casting algorithm)
  const isPointInPolygon = (point, polygon) => {
    if (!polygon || polygon.length < 3) return true // No boundary = allow all
    
    const x = parseFloat(point.lat)
    const y = parseFloat(point.lng)
    
    if (isNaN(x) || isNaN(y)) return true // Invalid point, skip check
    
    let inside = false
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = parseFloat(polygon[i].lat)
      const yi = parseFloat(polygon[i].lng)
      const xj = parseFloat(polygon[j].lat)
      const yj = parseFloat(polygon[j].lng)
      
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside
      }
    }
    return inside
  }

  // Validate all paddock coordinates are within ranch boundary
  const validatePaddockInRanch = (paddockCoords, ranchBoundary) => {
    if (!ranchBoundary || ranchBoundary.length < 3) {
      return { valid: true, message: '', warning: 'Ranch has no boundary set - skipping validation' }
    }

    const validPaddockCoords = paddockCoords.filter(c => c.lat && c.lng)
    const outsidePoints = []
    
    for (let i = 0; i < validPaddockCoords.length; i++) {
      const point = validPaddockCoords[i]
      if (!isPointInPolygon(point, ranchBoundary)) {
        outsidePoints.push(i + 1)
      }
    }
    
    if (outsidePoints.length > 0) {
      return { 
        valid: false, 
        message: `Point(s) #${outsidePoints.join(', #')} are outside the ranch boundary`,
        warning: ''
      }
    }
    
    return { valid: true, message: '', warning: '' }
  }

  // Validate coordinates whenever they change
  useEffect(() => {
    if (ranch?.boundary_coordinates) {
      const validation = validatePaddockInRanch(coordinates, ranch.boundary_coordinates)
      setBoundaryWarning(validation.warning || validation.message)
    }
  }, [coordinates, ranch])

  // Generate next paddock code
  const generatePaddockCode = () => {
    if (!existingPaddocks || existingPaddocks.length === 0) {
      return 'P01'
    }
    
    // Find the highest numbered paddock code
    const paddockNumbers = existingPaddocks
      .map(p => {
        const match = p.code?.match(/P(\d+)/)
        return match ? parseInt(match[1], 10) : 0
      })
      .filter(n => !isNaN(n))
    
    const maxNumber = Math.max(0, ...paddockNumbers)
    return `P${String(maxNumber + 1).padStart(2, '0')}`
  }

  // Calculate area from coordinates using Shoelace formula
  const calculatePolygonArea = (coords) => {
    const validCoords = coords.filter(c => c.lat && c.lng)
    if (validCoords.length < 3) return null

    const points = validCoords.map(c => ({
      lat: parseFloat(c.lat),
      lng: parseFloat(c.lng)
    }))

    const n = points.length
    let area = 0
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n
      area += points[i].lng * points[j].lat
      area -= points[j].lng * points[i].lat
    }
    area = Math.abs(area) / 2

    // Convert from degrees² to approximate m² then to hectares
    const metersPerDegree = 111320
    const areaM2 = area * metersPerDegree * metersPerDegree
    const areaHa = areaM2 / 10000

    return areaHa
  }

  // Calculate area when coordinates change
  useEffect(() => {
    const area = calculatePolygonArea(coordinates)
    setCalculatedArea(area)
  }, [coordinates])

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createPaddock,
    onSuccess: () => {
      queryClient.invalidateQueries(['paddocks'])
      navigate('/paddocks')
    },
    onError: (err) => {
      setError(err.response?.data?.detail || 'Failed to create paddock')
    },
  })

  const addCoordinate = () => {
    setCoordinates([...coordinates, { lat: '', lng: '' }])
  }

  const removeCoordinate = (index) => {
    if (coordinates.length > 3) {
      setCoordinates(coordinates.filter((_, i) => i !== index))
    }
  }

  const updateCoordinate = (index, field, value) => {
    const newCoords = [...coordinates]
    newCoords[index][field] = value
    setCoordinates(newCoords)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    if (!formData.name.trim()) {
      setError('Please enter a paddock name')
      return
    }

    const validCoords = coordinates.filter(c => c.lat && c.lng)
    if (validCoords.length < 3) {
      setError('Please enter at least 3 coordinate points')
      return
    }

    // Validate paddock is within ranch boundary
    if (ranch?.boundary_coordinates && ranch.boundary_coordinates.length >= 3) {
      const validation = validatePaddockInRanch(validCoords, ranch.boundary_coordinates)
      if (!validation.valid) {
        setError(`Paddock boundary error: ${validation.message}. All paddock points must be within the ranch boundary.`)
        return
      }
    }

    const paddockCode = generatePaddockCode()

    const payload = {
      ranch_id: ranchId,
      code: paddockCode,
      name: formData.name,
      area_ha: calculatedArea ? parseFloat(calculatedArea.toFixed(4)) : null,
      management_type: formData.management_type,
      has_kraal: formData.has_kraal,
      has_water: formData.has_water,
      boundary_coordinates: validCoords,
    }

    createMutation.mutate(payload)
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/paddocks')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Add New Paddock</h1>
          <p className="text-gray-600">Define a new paddock within your ranch</p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Ranch Boundary Info */}
      {ranch && (
        <div className={`mb-6 p-4 rounded-lg ${
          ranch.boundary_coordinates && ranch.boundary_coordinates.length >= 3
            ? 'bg-green-50 border border-green-200'
            : 'bg-yellow-50 border border-yellow-200'
        }`}>
          <div className="flex items-start gap-2">
            {ranch.boundary_coordinates && ranch.boundary_coordinates.length >= 3 ? (
              <>
                <MapPin className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800">
                    Ranch boundary is set ({ranch.boundary_coordinates.length} points)
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    Paddock coordinates will be validated to ensure they're within the ranch boundary.
                  </p>
                </div>
              </>
            ) : (
              <>
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">
                    Ranch boundary not set
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    <a href="/ranch/settings" className="underline">Set ranch boundary</a> to enable coordinate validation.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Boundary validation warning */}
      {boundaryWarning && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-800">Boundary Warning</p>
            <p className="text-sm text-yellow-700">{boundaryWarning}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Paddock Information</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paddock Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="e.g., North Pasture"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Management Type
              </label>
              <select
                value={formData.management_type}
                onChange={(e) => setFormData({ ...formData, management_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="mmg">MMG (Multi-paddock Mob Grazing)</option>
                <option value="rotational">Rotational</option>
                <option value="continuous">Continuous</option>
                <option value="strip">Strip Grazing</option>
                <option value="holding">Holding</option>
              </select>
            </div>

            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.has_water}
                  onChange={(e) => setFormData({ ...formData, has_water: e.target.checked })}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <Droplets className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-gray-700">Has Water</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.has_kraal}
                  onChange={(e) => setFormData({ ...formData, has_kraal: e.target.checked })}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <Home className="h-4 w-4 text-amber-500" />
                <span className="text-sm text-gray-700">Has Kraal</span>
              </label>
            </div>
          </div>
        </div>

        {/* Boundary Coordinates */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Boundary Coordinates</h2>
            </div>
            <button
              type="button"
              onClick={addCoordinate}
              className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
            >
              <Plus className="h-4 w-4" />
              Add Point
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Enter at least 3 GPS coordinate points to define the paddock boundary.
          </p>

          <div className="space-y-3">
            {coordinates.map((coord, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="text-sm text-gray-500 w-8">#{index + 1}</span>
                <input
                  type="number"
                  step="any"
                  placeholder="Latitude (e.g., 0.3476)"
                  value={coord.lat}
                  onChange={(e) => updateCoordinate(index, 'lat', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
                <input
                  type="number"
                  step="any"
                  placeholder="Longitude (e.g., 32.5825)"
                  value={coord.lng}
                  onChange={(e) => updateCoordinate(index, 'lng', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
                {coordinates.length > 3 && (
                  <button
                    type="button"
                    onClick={() => removeCoordinate(index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {calculatedArea && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                <strong>Calculated Area:</strong> {calculatedArea.toFixed(4)} hectares
              </p>
            </div>
          )}
        </div>

        {/* Auto-generated info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Auto-generated:</strong> Paddock code will be <strong>{generatePaddockCode()}</strong>
          </p>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/paddocks')}
            className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <Save className="h-5 w-5" />
            {createMutation.isPending ? 'Creating...' : 'Create Paddock'}
          </button>
        </div>
      </form>
    </div>
  )
}
